import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { POST } from "../../services/HTTPServices";
import { ALERT_TITLES, ROUTES } from "../../constants";
import {
  alertMessage,
  alertMessageWithCancel,
} from "../../services/alertService";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import {
  PageContainer,
  PageHeader,
  PageTitle,
  FormContainer,
  FormGroup,
  FormLabel,
  FormInput,
  MenuItemsContainer,
  MenuItemCard,
  MenuItemImage,
  MenuItemInfo,
  MenuItemName,
  MenuItemPrice,
  QuantityControls,
  QuantityButton,
  QuantityDisplay,
  RemoveItemButton,
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
  ErrorMessage,
  ActionButtons,
  ReviewModalOverlay,
  ReviewModalContent,
  ReviewModalHeader,
  ReviewModalTitle,
  ReviewModalCloseButton,
  ReviewModalBody,
  ReviewSection,
  ReviewSectionTitle,
  ReviewImageContainer,
  ReviewImagePlaceholder,
  ReviewDetailRow,
  ReviewDetailLabel,
  ReviewDetailValue,
  ReviewItemsList,
  ReviewItemCard,
  ReviewItemImage,
  ReviewItemInfo,
  ReviewItemName,
  ReviewItemQuantity,
  ReviewModalActions,
  ReviewModalButton,
  ReviewLoadingSpinner,
  ErrorMessageTitle,
  ResponsiveContainer,
  ResponsiveButtonContainer,
  HelpIconContainer,
  HelperTextPopup,
  HelperTextContent,
} from "../../styles/AddNewBundleDeal.styled";
import { LanguageText } from "../../styles/components";
import {
  SendButton,
  SendButtonActive,
} from "../../styles/AddNewFoodMenu.styled";
import { DropdownWrapperFullWidth } from "../../styles/FoodPage.styles";
import MultiSelectWithSearch from "./FoodEntry/MultiSelectDropdown";
import { setItem } from "../../utils/cache/LocalStorage";
import { FlexContainer } from "../../styles/PersonalInfoStep";
import CustomImageUpload from "./FoodEntry/ImageUpload";
import HelpIcon from "@mui/icons-material/Help";
import { useErrorResponse } from "../../services/useErrorResponse";

interface BundleFormData {
  id?: string;
  name: string;
  photoPath: string;
  price: number;
  items: Array<any>;
  isAvailable: boolean;
}

interface FormErrors {
  name?: string;
  price?: string;
  items?: string;
  photoPath?: string;
}

const AddNewBundleDeal: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const outErrorResponse = useErrorResponse();

  const [authUser, setAuthUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [items, setItems] = useState<any>([]);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [isCreatingFromReview, setIsCreatingFromReview] =
    useState<boolean>(false);

  const [refreshForm, setRefreshForm] = useState<boolean>(true);

  const [formData, setFormData] = useState<BundleFormData>({
    id: "",
    name: "",
    photoPath: "",
    price: 0,
    items: [],
    isAvailable: true,
  });

  const [selectedIdArr, setSelectedIdArr] = useState<any>([]);

  const [errors, setErrors] = useState<FormErrors>({});

  const isEditing = id && id !== "-1";

  // Helper text state management
  const [helperTextVisible, setHelperTextVisible] = useState<{
    bundlePhoto: boolean;
    bundleName: boolean;
    bundleMenu: boolean;
    bundlePrice: boolean;
    bundleAvailable: boolean;
  }>({
    bundlePhoto: false,
    bundleName: false,
    bundleMenu: false,
    bundlePrice: false,
    bundleAvailable: false,
  });

  const loadBundleDeal = useCallback(async () => {
    setLoading(true);
    const response: any = await POST(
      "fooddelivery/GetBundleDetailByBundleId",
      { bundleDealId: id + "" },
      "provider"
    );

    if (response && response.status === 200 && response.data) {
      setRefreshForm(false);

      const bundleData = response.data;
      setFormData({
        id: bundleData.id || "",
        name: bundleData.name || "",
        photoPath: bundleData.photoPath || "",
        price: bundleData.price || 0,
        items: bundleData.bundleItemList || [],
        isAvailable: bundleData.isAvailable || true,
      });

      let tempIds = bundleData.bundleItemList.map(
        (item: any) => item.foodMenuId
      );
      setSelectedIdArr(tempIds);
      setTimeout(() => {
        setRefreshForm(true);
      }, 500);

      setLoading(false);
    } else {
      setLoading(false);
      if (response.status && response.status === 401) {
        navigate("/unauthorized-page");
      } else {
        alertMessage(ALERT_TITLES.ERROR, response.message);
      }
    }
  }, [id, navigate]);

  const getDataProducts = async (id: any, page = 1) => {
    let param = {
      serviceProviderId: id.toString(),
      // eslint-disable-next-line eqeqeq
      categoryId: "",
      keyword: "",
      sortBy: {
        field: 1,
        order: 1,
      },
      pgNo: page,
      pgSize: 500,
    };

    const response: any = await POST(
      "fooddelivery/GetMenuByServiceProviderId",
      param,
      "provider"
    );

    if (response && response.status === 200 && response.data) {
      const data = response.data.getMenuByServiceProviderIdList;
      setItems(data);
      setLoading(false);
    } else {
      setLoading(false);
      if (response.status && response.status === 401) {
        navigate("/unauthorized-page");
      } else {
        alertMessage(ALERT_TITLES.ERROR, response.message);
      }
    }
  };

  useEffect(() => {
    setErrorMessage("")
    const _authLocal = JSON.parse(localStorage.getItem("authUser") || "null");
    if (_authLocal) {
      setAuthUser(_authLocal);
      getDataProducts(_authLocal.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEditing) {
      loadBundleDeal();
    }
  }, [id, isEditing, loadBundleDeal]);

  // Utility function to sanitize input by removing special characters
  const sanitizeInput = (input: string): string => {
    // Allow only letters, numbers, spaces, hyphens, apostrophes, and basic punctuation (.,()&)
    return input.replace(/[^a-zA-Z0-9\s\-'.,()&]/g, "");
  };

  const handleInputChange = (
    field: keyof BundleFormData,
    value: string | number
  ) => {
    // Sanitize name field to prevent special characters
    if (field === "name" && typeof value === "string") {
      value = sanitizeInput(value);
    }

    // If it's a price field, validate decimal places
    if (field === "price") {
      const valueStr = value.toString();
      // If more than 2 digits after decimal point, return
      if (/\.\d{3,}/.test(valueStr)) {
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));

    // Real-time validation for immediate error feedback
    setErrors((prev) => {
      const newErrors = { ...prev };

      // Real-time validation to set/clear errors immediately
      switch (field) {
        case "name":
          const isValidName =
            value &&
            value !== "" &&
            typeof value === "string" &&
            value.length <= 120;

          if (!isValidName) {
            newErrors.name =
              value === "" || !value
                ? "Bundle name is mandatory."
                : "Bundle Name must be within 120 characters.";
          } else {
            delete newErrors.name;
          }
          break;
        case "price":
          const numValue = Number(value);
          if (!value || numValue < 0) {
            newErrors.price =
              "Enter Bundle Price, Max 2 decimal point. Value must be greater or equal to 0.";
          } else {
            delete newErrors.price;
          }
          break;
        case "photoPath":
          if (!value || !value.toString().trim()) {
            newErrors.photoPath = "Bundle Photo is mandatory.";
          } else {
            delete newErrors.photoPath;
          }
          break;
        default:
          // For any other field, just remove the error
          delete newErrors[field as keyof FormErrors];
          break;
      }

      return newErrors;
    });
  };

  const handleQuantityChange = (index: number, change: number) => {
    const updatedItems = [...formData.items];
    const currentQuantity =
      updatedItems[index].quantity || updatedItems[index].qty || 1;
    const newQuantity = Math.max(1, currentQuantity + change);
    updatedItems[index] = {
      ...updatedItems[index],
      quantity: newQuantity,
      qty: newQuantity,
    };
    setFormData((prev) => ({ ...prev, items: updatedItems }));

    // Real-time validation for items error
    setErrors((prev) => {
      const newErrors = { ...prev };

      // Validate items after quantity change
      if (updatedItems.length === 0) {
        newErrors.items = "Minimum 1 menu item is required to create a bundle.";
      } else if (
        updatedItems.length === 1 &&
        updatedItems[0].quantity === 1
      ) {
        newErrors.items =
          "If only one menu item is selected, minimum quantity must be at least 2.";
      } else {
        // Clear items error if validation passes
        delete newErrors.items;
      }

      return newErrors;
    });
  };

  const deleteConfirm = async () => {
    const confirmLeave = await alertMessageWithCancel(
      ALERT_TITLES.INFO,
      "Are you sure to delete this bundle deal?"
    );
    if (confirmLeave) {
      deleteBundleDeal();
    }
  };

  const deleteBundleDeal = async () => {
    let url = "fooddelivery/DeleteBundleDeal";
    let res: any = await POST(url, { id: formData.id }, "provider");

    if (res && res.status === 200) {
      alertMessage(
        ALERT_TITLES.SUCCESS,
        "You deleted bundle deal successfully."
      );
      setLoading(false);
      setItem("selected_menu_tab", "bundle-deal");
      navigate(ROUTES.MENU_MANAGEMENT);
    } else {
      setLoading(false);
      outErrorResponse(res.status, res.message);
    }
  };

  const removeItem = (index: number) => {
    setRefreshForm(false);
    const itemToRemove = formData.items[index];

    // Remove from selectedIdArr
    const updatedSelectedIdArr = selectedIdArr.filter(
      // eslint-disable-next-line eqeqeq
      (id: any) => id != itemToRemove.foodMenuId
    );

    setSelectedIdArr(updatedSelectedIdArr);
    // Remove from formData.items
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, items: updatedItems }));
    setRefreshForm(true);
  };

  const ImageWrapper = () => {
    let paths = [];
    paths.push({
      id: 1,
      productId: 0,
      photoName: "",
      photoPath: formData.photoPath,
      isDelete: false,
    });
    return (
      <>
        <CustomImageUpload
          titleEN="Product Photos"
          titleCN="产品图片"
          data={paths}
          noOfImage={1}
          maxNumber={1}
          isRequired={true}
          callback={(value: any) => valueChangedProductPhotos(value)}
          productId={formData.id || -1}
        />
        {errors.photoPath && <ErrorMessage>{errors.photoPath}</ErrorMessage>}
      </>
    );
  };

  const valueChangedProductPhotos = (value: any) => {
    if (value.type === "401") {
      window.location.replace("/unauthorized-page");
    } else {
      let form = { ...formData };
      form.photoPath = value.data[0].photoPath;
      setFormData(form);

      // Clear photoPath error when photo is uploaded
      if (value.data[0].photoPath && value.data[0].photoPath.trim()) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.photoPath;
          return newErrors;
        });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (formData.photoPath === "") {
      newErrors.photoPath = "Bundle Photo is mandatory.";
    }

    const isValidName =
      formData.name &&
      formData.name !== "" &&
      typeof formData.name === "string" &&
      formData.name.length <= 120;

    if (!isValidName) {
      newErrors.name =
        formData.name === ""
          ? "Bundle name is mandatory."
          : "Bundle Name must be within 120 characters.";
    }

    if (formData.items.length === 0) {
      newErrors.items = "Minimum 1 menu item is required to create a bundle.";
    } else if (
      formData.items.length === 1 &&
      formData.items[0].quantity === 1
    ) {
      newErrors.items =
        "If only one menu item is selected, minimum quantity must be at least 2.";
    }

    if (!formData.price || formData.price < 0) {
      newErrors.price =
        "Enter Bundle Price, Max 2 decimal point. Value must be greater or equal to 0.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    // Show review modal before submission
    setShowReviewModal(true);
  };

  const handleConfirmSubmit = async () => {
    // Validate form again before API call to ensure consistency
    if (!validateForm()) {
      setShowReviewModal(false);
      return;
    }

    setLoading(true);
    setShowError(false);

    try {
      const endpoint = isEditing
        ? "fooddelivery/UpdateBundleDeal"
        : "fooddelivery/CreateBundleDeal";

      let payload;

      if (isEditing) {
        // Update API payload structure
        payload = {
          id: id,
          name: formData.name,
          serviceProviderId: authUser.id.toString(),
          price: parseFloat(formData.price?.toString()) || 0,
          photoPath: formData.photoPath || "",
          bundleItemList: formData.items.map((item: any) => ({
            id: item.id || "",
            foodMenuId: item.foodMenuId || "",
            qty: parseInt(item.quantity?.toString()) || 1,
          })),
        };
      } else {
        // Create API payload structure

        payload = {
          name: formData.name,
          serviceProviderId: authUser.id.toString(),
          price: parseFloat(formData.price?.toString()) || 0,
          photoPath: formData.photoPath || "",
          bundleItems: formData.items.map((item: any) => ({
            foodMenuId: item.foodMenuId || "",
            qty: parseInt(item.quantity?.toString()) || 1,
          })),
        };
      }

      const response = await POST(endpoint, payload, "provider");

      if (response && response.status === 200) {
        alertMessage(
          ALERT_TITLES.SUCCESS,
          isEditing
            ? "Bundle deal updated successfully!"
            : "Bundle created. Pending admin approval."
        );

        setTimeout(() => {
          setItem("selected_menu_tab", "bundle-deal");
          navigate(ROUTES.MENU_MANAGEMENT);
        }, 1000);
      } else {
        setLoading(false);
        if (response && response.status === 401) {
          navigate("/unauthorized-page");
        } else {
          const errorMsg =
            response && typeof response === "object" && "message" in response
              ? (response as any).message
              : "Failed to save bundle deal";
          alertMessage(ALERT_TITLES.ERROR, errorMsg);
        }
      }
    } catch (error) {
      setLoading(false);
      alertMessage(ALERT_TITLES.ERROR, "Failed to save bundle deal");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    const confirmLeave = await alertMessageWithCancel(
      ALERT_TITLES.INFO,
      "Are you sure to cancel your creating or editing food menu?"
    );
    if (confirmLeave) {
      setItem("selected_menu_tab", "bundle-deal");

      navigate(ROUTES.MENU_MANAGEMENT);
    }
  };

  const callBackDropdownMultipleItems = (selectedIds: string[]) => {
    const selectedItems = items.filter((item: any) =>
      selectedIds.includes(item.id)
    );

    // Extract only ID arrays and update selectedIdArr
    setSelectedIdArr(selectedIds);

    // Log the selectedIds parameter instead of the state (which hasn't updated yet)

    // Create new items to add to formData.items
    const newItemsToAdd = selectedItems.map((item: any) => ({
      id: "", // Add the missing id field
      name: item.item || item.name || item.foodMenuName,
      price: item.price || 0,
      photoPath: item.photoPath || "",
      quantity: 1,
      foodMenuId: item.id || item.foodMenuId,
      qty: 1,
    }));

    // Handle existing items and update quantities
    const updatedItems = [...formData.items];

    newItemsToAdd.forEach((newItem: any) => {
      const existingIndex = updatedItems.findIndex(
        (item) => item.foodMenuId === newItem.foodMenuId // Now this comparison will work
      );

      if (existingIndex > -1) {
        // Update quantity if item already exists
        // updatedItems[existingIndex].quantity =
        //   (updatedItems[existingIndex].quantity || 1) + 1;
        // updatedItems[existingIndex].qty =
        //   (updatedItems[existingIndex].qty || 1) + 1;
      } else {
        // Add new item
        updatedItems.push(newItem);
      }
    });

    setFormData({
      ...formData,
      items: updatedItems,
    });

    // Clear items error when items are selected
    if (updatedItems.length > 0) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.items;
        return newErrors;
      });
    }
  };

  // Helper text functions
  const toggleHelperText = (field: keyof typeof helperTextVisible) => {
    setHelperTextVisible((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const hideAllHelperText = () => {
    setHelperTextVisible({
      bundlePhoto: false,
      bundleName: false,
      bundleMenu: false,
      bundlePrice: false,
      bundleAvailable: false,
    });
  };

  // Close helper text when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".help-icon-container")) {
        hideAllHelperText();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Review Modal Helper Functions
  const handleCloseReview = () => {
    setShowReviewModal(false);
  };

  const handleCreateFromReview = async () => {
    setIsCreatingFromReview(true);
    await handleConfirmSubmit();
    setIsCreatingFromReview(false);
    setShowReviewModal(false);
  };

  return (
    <>
      {refreshForm && (
        <PageContainer>
          {loading && (
            <LoadingOverlay>
              <LoadingSpinner>
                <LoadingSpinnerIcon />
              </LoadingSpinner>
            </LoadingOverlay>
          )}

          <PageHeader>
            {/* <BackButton onClick={handleCancel}>Back</BackButton> */}
            {id === "-1" && (
              <PageTitle>
                <LanguageText>Add Food Bundle</LanguageText>
              </PageTitle>
            )}
            {id !== "-1" && (
              <PageTitle>
                <LanguageText>Edit Food Bundle</LanguageText>
              </PageTitle>
            )}
          </PageHeader>

          <div className="mb-16">
            {showError && <ErrorMessageTitle>{errorMessage}</ErrorMessageTitle>}
          </div>

          <FormContainer>
            {/* Bundle Photo */}
            <FormGroup>
              <FormLabel>
                Bundle Photo*{" "}
                <HelpIconContainer
                  className="help-icon-container"
                  onMouseOver={() => toggleHelperText("bundlePhoto")}
                  onMouseOut={() => toggleHelperText("bundlePhoto")}
                >
                  <HelpIcon
                    sx={{
                      fontSize: "24px",
                      color: "#CCCCCC",
                      cursor: "pointer",
                      ml: 0.5,
                    }}
                  />
                  {helperTextVisible.bundlePhoto && (
                    <HelperTextPopup $visible={helperTextVisible.bundlePhoto}>
                      <HelperTextContent>
                        Upload a high-quality photo of your bundle deal to
                        attract customers.
                      </HelperTextContent>
                    </HelperTextPopup>
                  )}
                </HelpIconContainer>
              </FormLabel>
              <ResponsiveContainer>
                <div className="mb-16">{ImageWrapper()}</div>
              </ResponsiveContainer>
            </FormGroup>

            {/* Bundle Name */}
            <FormGroup>
              <FormLabel>
                Bundle Name(Max. 150)*
                <HelpIconContainer
                  className="help-icon-container"
                  onMouseOver={() => toggleHelperText("bundleName")}
                  onMouseOut={() => toggleHelperText("bundleName")}
                >
                  <HelpIcon
                    sx={{
                      fontSize: "24px",
                      color: "#CCCCCC",
                      cursor: "pointer",
                      ml: 0.5,
                    }}
                  />
                  {helperTextVisible.bundleName && (
                    <HelperTextPopup $visible={helperTextVisible.bundleName}>
                      <HelperTextContent>
                        Enter a catchy name for your bundle deal that customers
                        will easily recognize.
                      </HelperTextContent>
                    </HelperTextPopup>
                  )}
                </HelpIconContainer>
              </FormLabel>
              <ResponsiveContainer>
                <FormInput
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter Bundle Name"
                  maxLength={150}
                  $hasError={!!errors.name}
                  $width={680}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </ResponsiveContainer>
            </FormGroup>

            {/* Bundle Menu */}
            <FormGroup>
              <FormLabel>
                Bundle Menu*
                <HelpIconContainer
                  className="help-icon-container"
                  onMouseOver={() => toggleHelperText("bundleMenu")}
                  onMouseOut={() => toggleHelperText("bundleMenu")}
                >
                  <HelpIcon
                    sx={{
                      fontSize: "24px",
                      color: "#CCCCCC",
                      cursor: "pointer",
                      ml: 0.5,
                    }}
                  />
                  {helperTextVisible.bundleMenu && (
                    <HelperTextPopup $visible={helperTextVisible.bundleMenu}>
                      <HelperTextContent>
                        Select multiple menu items to include in your bundle
                        deal. Customers will get all selected items at a
                        discounted price.
                      </HelperTextContent>
                    </HelperTextPopup>
                  )}
                </HelpIconContainer>
              </FormLabel>
              <ResponsiveContainer>
                <DropdownWrapperFullWidth>
                  <MultiSelectWithSearch
                    data={items}
                    default={selectedIdArr}
                    borderColor="#e39435"
                    bgColor="#FFF"
                    color="#1E2F23"
                    arrowColor="#76B041"
                    menuItemColor="#000000"
                    id="id"
                    value="item"
                    height={56}
                    fontSize={16}
                    fontWeight={700}
                    placeHolder="+ Add Items"
                    callback={(selectedIds: string[]) => {
                      callBackDropdownMultipleItems(selectedIds);
                    }}
                  />
                </DropdownWrapperFullWidth>

                {formData.items.length > 0 && (
                  <MenuItemsContainer>
                    {formData.items.map((item, index) => (
                      <MenuItemCard key={index}>
                        <MenuItemImage>
                          <img
                            src={item.photoPath || "/placeholder-food.jpg"}
                            alt={item.foodMenuName}
                          />
                        </MenuItemImage>
                        <MenuItemInfo>
                          <MenuItemName>
                            {item.name ||
                              item.item ||
                              item.foodMenuName ||
                              "Selected Item"}
                          </MenuItemName>
                          <MenuItemPrice>
                            ${(item.price || 0).toFixed(2)}
                          </MenuItemPrice>
                        </MenuItemInfo>
                        <FlexContainer>
                          <QuantityControls>
                            <QuantityButton
                              onClick={() => handleQuantityChange(index, -1)}
                            >
                              <RemoveIcon sx={{ fontSize: 24 }} />
                            </QuantityButton>
                            <QuantityDisplay>
                              {item.quantity || item.qty || 1}
                            </QuantityDisplay>
                            <QuantityButton
                              onClick={() => handleQuantityChange(index, 1)}
                            >
                              <AddIcon sx={{ fontSize: 24 }} />
                            </QuantityButton>
                          </QuantityControls>
                          <RemoveItemButton onClick={() => removeItem(index)}>
                            <DeleteOutlinedIcon sx={{ fontSize: "24px" }} />
                            Delete
                          </RemoveItemButton>
                        </FlexContainer>
                      </MenuItemCard>
                    ))}
                  </MenuItemsContainer>
                )}
                {errors.items && <ErrorMessage>{errors.items}</ErrorMessage>}
              </ResponsiveContainer>
            </FormGroup>

            {/* Bundle Price */}
            <FormGroup>
              <FormLabel>
                Bundle Price (SGD)*
                <HelpIconContainer
                  className="help-icon-container"
                  onMouseOver={() => toggleHelperText("bundlePrice")}
                  onMouseOut={() => toggleHelperText("bundlePrice")}
                >
                  <HelpIcon
                    sx={{
                      fontSize: "24px",
                      color: "#CCCCCC",
                      cursor: "pointer",
                      ml: 0.5,
                    }}
                  />
                  {helperTextVisible.bundlePrice && (
                    <HelperTextPopup $visible={helperTextVisible.bundlePrice}>
                      <HelperTextContent>
                        Set a competitive price for your bundle. Make sure it's{" "}
                        <span className="highlight">
                          lower than the individual item prices
                        </span>{" "}
                        to provide value to customers.
                      </HelperTextContent>
                    </HelperTextPopup>
                  )}
                </HelpIconContainer>
              </FormLabel>
              <ResponsiveContainer>
                <FormInput
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    handleInputChange("price", parseFloat(e.target.value) || 0)
                  }
                  $hasError={!!errors.price}
                  placeholder="Enter Bundle Price, Max 2 decimal point"
                  $width={680}
                />
                {errors.price && <ErrorMessage>{errors.price}</ErrorMessage>}
              </ResponsiveContainer>
            </FormGroup>
            {/* Availability Toggle */}
            {/* <FormGroup>
              <FormLabel>
                <div>
                  <LanguageText className="f-16 fw-700 primary-color">
                    Bundle Available*
                  </LanguageText>
                  <HelpIconContainer
                    className="help-icon-container"
                    onClick={() => toggleHelperText("bundleAvailable")}
                  >
                    <HelpIcon
                      sx={{
                        fontSize: "24px",
                        color: "#CCCCCC",
                        cursor: "pointer",
                        ml: 0.5,
                      }}
                    />
                    {helperTextVisible.bundleAvailable && (
                      <HelperTextPopup
                        $visible={helperTextVisible.bundleAvailable}
                      >
                        <HelperTextContent>
                          Toggle this to control whether the menu item is
                          available for ordering.
                        </HelperTextContent>
                      </HelperTextPopup>
                    )}
                  </HelpIconContainer>
                </div>
              </FormLabel>
              <ResponsiveContainer>
                <AvailabilityToggleContainer>
                  <ToggleSwitch>
                    <ToggleInput
                      type="checkbox"
                      checked={formData?.isAvailable || true}
                      onChange={handleAvailabilityToggle}
                    />
                    <ToggleSlider />
                  </ToggleSwitch>
                  <ToggleLabel></ToggleLabel>
                </AvailabilityToggleContainer>
              </ResponsiveContainer>
            </FormGroup> */}

            <br></br>
          </FormContainer>

          <ActionButtons>
            <SendButton onClick={cancel}>
              <LanguageText>Back</LanguageText>
            </SendButton>
            <ResponsiveButtonContainer>
              {id !== "-1" && (
                <SendButton className="mr-16 mb-8" onClick={deleteConfirm}>
                  <LanguageText>Delete</LanguageText>
                </SendButton>
              )}
              <SendButtonActive onClick={handleSubmit}>
                <LanguageText>
                  {loading
                    ? "Saving..."
                    : id !== "-1"
                    ? "Update Bundle Deal"
                    : "Create Bundle Deal"}
                </LanguageText>
              </SendButtonActive>
            </ResponsiveButtonContainer>
          </ActionButtons>
        </PageContainer>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModalOverlay>
          <ReviewModalContent>
            <ReviewModalHeader>
              <ReviewModalTitle>Review Bundle Deal</ReviewModalTitle>
              <ReviewModalCloseButton onClick={handleCloseReview}>
                ×
              </ReviewModalCloseButton>
            </ReviewModalHeader>

            <ReviewModalBody>
              {/* Bundle Image */}
              <ReviewSection>
                <ReviewSectionTitle>📸 Bundle Image</ReviewSectionTitle>
                {formData.photoPath ? (
                  <ReviewImageContainer>
                    <img src={formData.photoPath} alt="Bundle_Image" />
                  </ReviewImageContainer>
                ) : (
                  <ReviewImagePlaceholder>
                    No image uploaded
                  </ReviewImagePlaceholder>
                )}
              </ReviewSection>

              {/* Bundle Details */}
              <ReviewSection>
                <ReviewSectionTitle>📋 Bundle Details</ReviewSectionTitle>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Bundle Name:</ReviewDetailLabel>
                  <ReviewDetailValue className="crop_text_cart_300">
                    {formData.name || "Untitled Bundle"}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Bundle Price:</ReviewDetailLabel>
                  <ReviewDetailValue className="price">
                    ${formData.price?.toFixed(2) || "0.00"}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>Total Items:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    {formData.items.length} items
                  </ReviewDetailValue>
                </ReviewDetailRow>
                {/* <ReviewDetailRow>
                  <ReviewDetailLabel>Individual Value:</ReviewDetailLabel>
                  <ReviewDetailValue>
                    ${getTotalBundleValue().toFixed(2)}
                  </ReviewDetailValue>
                </ReviewDetailRow>
                <ReviewDetailRow>
                  <ReviewDetailLabel>You Save:</ReviewDetailLabel>
                  <ReviewDetailValue className="price">
                    $
                    {getSavingsAmount() > 0
                      ? getSavingsAmount().toFixed(2)
                      : "0.00"}
                  </ReviewDetailValue>
                </ReviewDetailRow> */}
              </ReviewSection>

              {/* Bundle Items */}
              <ReviewSection>
                <ReviewSectionTitle>🍽️ Bundle Items</ReviewSectionTitle>
                {formData.items.length > 0 ? (
                  <ReviewItemsList>
                    {formData.items.map((item, index) => (
                      <ReviewItemCard key={index}>
                        <ReviewItemImage>
                          <img
                            src={item.photoPath || "/placeholder-food.jpg"}
                            alt={item.name}
                          />
                        </ReviewItemImage>
                        <ReviewItemInfo>
                          <ReviewItemName>
                            {item.name || item.foodMenuName || "Selected Item"}
                          </ReviewItemName>
                          {/* <ReviewItemPrice>
                            ${(item.price || 0).toFixed(2)} each
                          </ReviewItemPrice> */}
                        </ReviewItemInfo>
                        <ReviewItemQuantity>
                          Qty: {item.quantity || item.qty || 1}
                        </ReviewItemQuantity>
                      </ReviewItemCard>
                    ))}
                  </ReviewItemsList>
                ) : (
                  <ReviewDetailValue>No items selected.</ReviewDetailValue>
                )}
              </ReviewSection>
            </ReviewModalBody>

            <ReviewModalActions>
              <ReviewModalButton
                variant="secondary"
                onClick={handleCloseReview}
              >
                Cancel
              </ReviewModalButton>
              <ReviewModalButton
                variant="primary"
                onClick={handleCreateFromReview}
                disabled={isCreatingFromReview}
              >
                {isCreatingFromReview && <ReviewLoadingSpinner />}
                {isCreatingFromReview
                  ? "Creating..."
                  : id !== "-1"
                  ? "Update Bundle Deal"
                  : "Create Bundle Deal"}
              </ReviewModalButton>
            </ReviewModalActions>
          </ReviewModalContent>
        </ReviewModalOverlay>
      )}
    </>
  );
};

export default AddNewBundleDeal;
